import { PmsTooltip } from '@/components/ui/pms-tooltip';
import { cn } from '@/lib/utils';
import { isExternalTarget, safeExternalHref } from '@/lib/word-links';
import type { Word, WordSummary } from '@/schemas/word';
import { useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/core';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CreateWordModal } from './create-word-modal';

interface ToolbarPosition {
  x: number;
  y: number;
}

type DisplayType = 'paragraph' | 'heading1' | 'heading2' | 'heading3';

const DISPLAY_TYPE_LABELS: Record<DisplayType, string> = {
  paragraph: 'Texte',
  heading1: 'Titre 1',
  heading2: 'Titre 2',
  heading3: 'Titre 3',
};

const DISPLAY_TYPES: DisplayType[] = ['paragraph', 'heading1', 'heading2', 'heading3'];

interface FloatingToolbarProps {
  editor: Editor;
  editorRef: React.RefObject<HTMLDivElement | null>;
  wordList: WordSummary[];
}

/**
 * Dark floating toolbar appearing above text selection in the editor.
 *
 * Tool groups:
 * 1. Block type selector (paragraph / headings)
 * 2. Inline formatting (bold, italic, strikethrough)
 * 3. Internal link — looks up selected text in the word list and applies the
 *    wordLink mark; opens CreateWordModal when no match is found.
 * 4. External link — URL input popover, applies the (plain) link mark.
 *
 * Selection tracking uses the browser's own Selection API (TipTap's
 * contentEditable is a real one), so positioning logic is unchanged from the
 * pre-TipTap implementation — only the actions were rewired to editor commands.
 */
export function FloatingToolbar({ editor, editorRef, wordList }: FloatingToolbarProps) {
  const queryClient = useQueryClient();
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState<ToolbarPosition>({ x: 0, y: 0 });
  const [showBelow, setShowBelow] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [linkMode, setLinkMode] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const linkInputRef = useRef<HTMLInputElement>(null);

  // Saved selection {from,to} — plain numbers survive the CreateWordModal's
  // async round-trip far more reliably than a cloned DOM Range ever did.
  const savedRangeRef = useRef<{ from: number; to: number } | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [pendingText, setPendingText] = useState('');

  useEffect(() => {
    function handleSelectionChange() {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setVisible(false);
        setDropdownOpen(false);
        setLinkMode(false);
        return;
      }

      const range = sel.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const editorEl = editorRef.current;
      if (!editorEl || !editorEl.contains(container)) {
        setVisible(false);
        return;
      }

      if (editor.isActive('codeBlock')) {
        setVisible(false);
        return;
      }

      const rect = range.getBoundingClientRect();
      if (!rect.width && !rect.height) {
        setVisible(false);
        return;
      }

      const TOOLBAR_W = 380;
      const TOOLBAR_H = 40;
      const GAP = 8;

      const x = Math.max(
        16,
        Math.min(window.innerWidth - TOOLBAR_W - 16, rect.left + rect.width / 2 - TOOLBAR_W / 2),
      );
      const spaceAbove = rect.top - GAP;
      const below = spaceAbove < TOOLBAR_H;
      setPosition({ x, y: below ? rect.bottom + GAP : rect.top - TOOLBAR_H - GAP });
      setShowBelow(below);
      setVisible(true);
    }

    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [editor, editorRef]);

  useEffect(() => {
    if (linkMode) linkInputRef.current?.focus();
  }, [linkMode]);

  function getDisplayType(): DisplayType {
    if (editor.isActive('heading', { level: 1 })) return 'heading1';
    if (editor.isActive('heading', { level: 2 })) return 'heading2';
    if (editor.isActive('heading', { level: 3 })) return 'heading3';
    return 'paragraph';
  }

  function handleTypeSelect(displayType: DisplayType) {
    const chain = editor.chain().focus();
    if (displayType === 'paragraph') chain.setParagraph().run();
    else chain.setNode('heading', { level: Number(displayType.slice(-1)) }).run();
    setDropdownOpen(false);
  }

  // Saves the selection before any state change; the modal's autoFocus would
  // otherwise collapse the live browser selection before onConfirm runs.
  function handleInternalLink(e: React.MouseEvent) {
    e.preventDefault(); // preserve selection

    const { from, to, empty } = editor.state.selection;
    if (empty) return;
    const text = editor.state.doc.textBetween(from, to).trim();
    if (!text) return;

    savedRangeRef.current = { from, to };

    const needle = text.toLowerCase();
    const word = wordList.find((w) => w.title.toLowerCase() === needle);

    if (word) {
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .setWordLink({ id: word.numericId })
        .run();
      savedRangeRef.current = null;
    } else {
      setPendingText(text);
      setCreateModalOpen(true);
    }
  }

  function handleCreateConfirm(word: Word) {
    void queryClient.invalidateQueries({ queryKey: ['words', 'all'] });
    setCreateModalOpen(false);
    const range = savedRangeRef.current;
    savedRangeRef.current = null;
    if (!range) return;
    editor.chain().focus().setTextSelection(range).setWordLink({ id: word.numericId }).run();
  }

  function handleLinkConfirm() {
    const raw = linkValue.trim();
    setLinkMode(false);
    setLinkValue('');
    if (!raw) return;
    const { empty } = editor.state.selection;
    if (empty) return;
    // A bare domain (no scheme) is assumed https; a target that already carries
    // a scheme must be one we allow. javascript:/data:/unknown schemes are
    // dropped rather than turned into a live, clickable anchor.
    const candidate = isExternalTarget(raw) ? raw : `https://${raw}`;
    const href = safeExternalHref(candidate);
    if (href === null) return;
    editor.chain().focus().setExternalLink({ href }).run();
  }

  const currentDisplayType = getDisplayType();
  const currentLabel = DISPLAY_TYPE_LABELS[currentDisplayType];
  const boldActive = editor.isActive('bold');
  const italicActive = editor.isActive('italic');
  const strikeActive = editor.isActive('strike');

  if (!visible) {
    return createModalOpen
      ? createPortal(
          <CreateWordModal
            selectedText={pendingText}
            onConfirm={handleCreateConfirm}
            onClose={() => setCreateModalOpen(false)}
          />,
          document.body,
        )
      : null;
  }

  const toolbar = (
    <>
      <div
        className={`ft${showBelow ? ' ft--below' : ''}${linkMode ? ' is-link' : ''}`}
        style={{ position: 'fixed', left: position.x, top: position.y, zIndex: 9999 }}
        role="toolbar"
        aria-label="Mise en forme du texte"
      >
        {linkMode ? (
          <div className="ft__link-wrap">
            <input
              ref={linkInputRef}
              className="ft__link-input"
              type="url"
              placeholder="https://…"
              value={linkValue}
              aria-label="URL du lien externe"
              onChange={(e) => setLinkValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleLinkConfirm();
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setLinkMode(false);
                  setLinkValue('');
                }
              }}
            />
            <PmsTooltip label="Confirmer">
              <button
                type="button"
                className="ft__btn ft__link-confirm"
                aria-label="Confirmer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleLinkConfirm();
                }}
              >
                <span className="material-symbols-rounded">check</span>
              </button>
            </PmsTooltip>
            <PmsTooltip label="Annuler">
              <button
                type="button"
                className="ft__btn ft__link-cancel"
                aria-label="Annuler"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setLinkMode(false);
                  setLinkValue('');
                }}
              >
                <span className="material-symbols-rounded">close</span>
              </button>
            </PmsTooltip>
          </div>
        ) : (
          <>
            {/* Group 1: block type selector */}
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                className={`ft__block${dropdownOpen ? ' is-open' : ''}`}
                aria-haspopup="menu"
                aria-expanded={dropdownOpen}
                onMouseDown={(e) => {
                  e.preventDefault();
                  setDropdownOpen((o) => !o);
                }}
              >
                {currentLabel}
                <span className="material-symbols-rounded chevron">expand_more</span>
              </button>

              {dropdownOpen && (
                <div className="ft__dropdown" role="menu" tabIndex={-1} aria-label="Type de bloc">
                  {DISPLAY_TYPES.map((displayType) => (
                    <button
                      key={displayType}
                      type="button"
                      role="menuitem"
                      aria-pressed={displayType === currentDisplayType}
                      className={`ft__opt${displayType === currentDisplayType ? ' is-current' : ''}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        handleTypeSelect(displayType);
                      }}
                    >
                      <span
                        className={cn(
                          'ft__opt-label',
                          displayType !== 'paragraph' && `ft__opt-label-${displayType}`,
                        )}
                      >
                        {DISPLAY_TYPE_LABELS[displayType]}
                      </span>
                      <span className="material-symbols-rounded check">check</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="ft__sep" />

            {/* Group 2: inline formatting toggles */}
            <PmsTooltip label="Gras">
              <button
                type="button"
                className={`ft__btn${boldActive ? ' is-active' : ''}`}
                aria-label="Gras"
                aria-pressed={boldActive}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleBold().run();
                }}
              >
                <span className="material-symbols-rounded">format_bold</span>
              </button>
            </PmsTooltip>
            <PmsTooltip label="Italique">
              <button
                type="button"
                className={`ft__btn${italicActive ? ' is-active' : ''}`}
                aria-label="Italique"
                aria-pressed={italicActive}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleItalic().run();
                }}
              >
                <span className="material-symbols-rounded">format_italic</span>
              </button>
            </PmsTooltip>
            <PmsTooltip label="Barré">
              <button
                type="button"
                className={`ft__btn${strikeActive ? ' is-active' : ''}`}
                aria-label="Barré"
                aria-pressed={strikeActive}
                onMouseDown={(e) => {
                  e.preventDefault();
                  editor.chain().focus().toggleStrike().run();
                }}
              >
                <span className="material-symbols-rounded">format_strikethrough</span>
              </button>
            </PmsTooltip>

            <div className="ft__sep" />

            {/* Group 3: links */}
            {/* Internal link: look up selected text in the word list */}
            <PmsTooltip label="Lier à un mot">
              <button
                type="button"
                className="ft__btn"
                aria-label="Lien interne"
                onMouseDown={handleInternalLink}
              >
                <span className="material-symbols-rounded">link</span>
              </button>
            </PmsTooltip>

            {/* External link: URL input popover */}
            <PmsTooltip label="Lien externe">
              <button
                type="button"
                className="ft__btn"
                aria-label="Lien externe"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setLinkMode(true);
                  setLinkValue('');
                }}
              >
                <span className="material-symbols-rounded">open_in_new</span>
              </button>
            </PmsTooltip>
          </>
        )}
      </div>

      {createModalOpen && (
        <CreateWordModal
          selectedText={pendingText}
          onConfirm={handleCreateConfirm}
          onClose={() => setCreateModalOpen(false)}
        />
      )}
    </>
  );

  return createPortal(toolbar, document.body);
}
